import { translate } from '@teamleader/i18n';
import { AnimationItem } from 'lottie-web';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';

import { useIsExtraLarge, useIsHuge, useIsSmall } from '../../../../../Components/Context/Hooks/mediaQueries';
import useLogoPosition from '../../../../../Components/Context/Hooks/useLogoPosition';
import Box from '../../../../../Components/UI/Box';
import Branding from '../../../../../Components/UI/Branding';
import Button from '../../../../../Components/UI/Button';
import FormItem from '../../../../../Components/UI/FormItem';
import FormLabel from '../../../../../Components/UI/FormLabel';
import Input from '../../../../../Components/UI/Input';
import Lottie from '../../../../../Components/UI/Lottie';
import MobilePopup from '../../../../../Components/UI/MobilePopup';
import OverflowingFlexBox from '../../../../../Components/UI/OverflowingFlexBox';
import Sidebar, { SIZES } from '../../../../../Components/UI/Sidebar';
import Textarea from '../../../../../Components/UI/Textarea';
import Plane from '../../../../../Svg/120x120_plane.svg';
import Send from '../../../../../Svg/120x120_send.svg';
import sending from '../../../../../Svg/Lottie/sending.json';
import { EVENTS, track } from '../../../../../Utils/analytics';
import { canOpenNavigationForInvoice } from '../../../../../Utils/canOpenNavigation';
import { captureException } from '../../../../../Utils/sentry';
import useGlobalContext, { InvoiceContext } from '../../../Context';
import ErrorMessage from '../../ErrorMessage';
import SuccessMessage from '../../SuccessMessage';
import * as styles from '../../styles.css';

interface FeedbackFormData {
  subject?: string;
  content?: string;
}

enum SENDING_STATE {
  IDLE = 'idle',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  FAILURE = 'failure',
}

const InvoiceFeedbackMenu: React.FC = () => {
  const { handleCloseMenu, request, type, openMenu } = useGlobalContext();
  const { invoice, openInvoices, paidInvoices, creditedInvoices } = useContext(InvoiceContext);
  const isSmall = useIsSmall();
  const isExtraLarge = useIsExtraLarge();
  const isHuge = useIsHuge();
  const [sendingState, setSendingState] = useState(SENDING_STATE.IDLE);
  const animation = useRef<AnimationItem>(null);
  const Element = isSmall ? MobilePopup : Sidebar;
  const { inSuccessMessage } = useLogoPosition(
    canOpenNavigationForInvoice(openInvoices?.data, paidInvoices?.data, creditedInvoices?.data),
  );

  useEffect(() => setSendingState(SENDING_STATE.IDLE), [openMenu?.time]);

  const handleSubmit = useCallback(
    (formValues: FeedbackFormData) => {
      (async () => {
        setSendingState(SENDING_STATE.PROCESSING);

        try {
          await request!('/invoices.sendFeedback', {
            body: JSON.stringify({
              id: invoice!.id,
              subject: formValues.subject,
              content: formValues.content,
            }),
          });
        } catch (error) {
          setSendingState(SENDING_STATE.FAILURE);
          captureException(error);
          return;
        }

        await new Promise<void>((resolve) => {
          if (!animation.current) {
            resolve();
            return;
          }

          animation.current.addEventListener('loopComplete', () => resolve());
        });
        animation.current?.removeEventListener('loopComplete');

        setSendingState(SENDING_STATE.SUCCESS);
        track(EVENTS.SENT_INVOICE_FEEDBACK, {
          invoice: invoice!.id,
        });
      })();
    },
    [invoice, request],
  );

  const handleValidate = useCallback(({ subject, content }: FeedbackFormData) => {
    const errors: Record<string, boolean> = {};

    if (!subject || subject.trim() === '') {
      errors.subject = true;
    }

    if (!content || content.trim() === '') {
      errors.content = true;
    }

    return errors;
  }, []);

  const handleCloseFeedbackMenu = useCallback(() => {
    if (sendingState === SENDING_STATE.IDLE) {
      track(EVENTS.CANCELLED_ADDING_INVOICE_FEEDBACK);
    }

    handleCloseMenu!();
  }, [handleCloseMenu, sendingState]);

  if (!invoice) {
    return null;
  }

  return (
    <Form
      key={`${invoice.id} ${openMenu ? openMenu.time.toString() : ''}`}
      onSubmit={handleSubmit}
      validate={handleValidate}
      render={({ handleSubmit, invalid }) => (
        <Element
          className={styles['menu']}
          display="flex"
          flexDirection="column"
          element="form"
          onSubmit={handleSubmit}
          onClose={sendingState !== SENDING_STATE.PROCESSING ? handleCloseFeedbackMenu : undefined}
          size={isHuge ? SIZES.WIDEST : isExtraLarge ? SIZES.WIDER : undefined}
        >
          {sendingState === SENDING_STATE.SUCCESS && (
            <SuccessMessage
              icon={<Send />}
              title={translate('feedbackSidebar.sentMessageTitle')}
              description={translate('feedbackSidebar.sentMessageToDepartmentText', {
                departmentName: invoice.department.name,
              })}
              branding={
                inSuccessMessage ? (
                  <Branding language={invoice.client.language} country={invoice.client.country} type={type} />
                ) : undefined
              }
            />
          )}
          {sendingState === SENDING_STATE.FAILURE && (
            <ErrorMessage
              icon={<Send />}
              title={translate('feedbackSidebar.errorMessageTitle')}
              description={translate('feedbackSidebar.errorMessageText')}
            />
          )}
          <Box
            flex={1}
            flexDirection="column"
            alignItems="center"
            className={styles['message']}
            paddingHorizontal={4}
            display={sendingState === SENDING_STATE.PROCESSING ? 'flex' : 'none'}
          >
            <Lottie
              ref={animation}
              animationData={sending}
              loop
              play={sendingState === SENDING_STATE.PROCESSING}
              className={styles['message-icon']}
            >
              <Plane />
            </Lottie>
            <h2 className={styles['message-title']}>{translate('feedbackSidebar.sendingMessageTitle')}</h2>
            <p className={styles['message-description']}>{translate('feedbackSidebar.sendingMessageText')}</p>
          </Box>
          {sendingState === SENDING_STATE.IDLE && (
            <>
              <OverflowingFlexBox>
                <Box paddingBottom={4} paddingHorizontal={4} className={styles['container']}>
                  <FormItem label={<FormLabel>{translate('feedbackSidebar.subject')}</FormLabel>}>
                    <Field
                      name="subject"
                      initialValue={translate('feedbackSidebar.subject.placeholder', {
                        quotationName: translate('title.invoice', { invoiceNumber: invoice.invoiceNumber }),
                      })}
                      render={({ input, meta }): React.ReactNode => (
                        <Input {...input} hasError={meta.touched && meta.error} />
                      )}
                    />
                  </FormItem>
                  <FormItem
                    label={<FormLabel>{translate('feedbackSidebar.content')}</FormLabel>}
                    className={styles['content']}
                  >
                    <Field
                      name="content"
                      render={({ input, meta }): React.ReactNode => (
                        <Textarea {...input} hasError={meta.touched && meta.error} />
                      )}
                    />
                  </FormItem>
                </Box>
              </OverflowingFlexBox>
              <Element.Footer padding={4} display="flex" justifyContent="flex-end">
                <Button level="secondary" type="button" marginRight={3} onClick={handleCloseFeedbackMenu}>
                  {translate('actions.cancel')}
                </Button>
                <Button level="primary" disabled={invalid} placement="left">
                  {translate('actions.send')}
                </Button>
              </Element.Footer>
            </>
          )}
        </Element>
      )}
    />
  );
};

export default InvoiceFeedbackMenu;
